import type { FunctionComponent } from "react";
import { useEffect, useState } from "react";
import type { IColumnsPickerLiteProps } from "@similarweb/ui-components";
import { ColumnsPickerLite } from "@similarweb/ui-components";
import { SearchInput } from "@similarweb/ui-components";
import { DownloadButtonMenu } from "app/components/React/DownloadButtonMenu/DownloadButtonMenu";
import { i18nFilter } from "app/filters/i18nFilter";
import { changeFilter } from "app/filters/ngFilters";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import DurationService from "app/services/DurationService";
import CountryService from "app/services/CountryService";
import { useCurrentStateAndParams } from "@uirouter/react";
import { DownloadExcelContainer } from "app/pages/workspace/StyledComponent";
import { DefaultFetchService } from "app/services/fetchService";
import { apiHelper } from "app/common/services/apiHelper";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import {
    MetricsCompareBarsContainer,
    MetricsCompareBarsStyled,
    MetricsCompareBarsTitle,
    SearchContainer,
    SectionContainer,
    Separator,
    TopHeadlineContainer,
} from "./StyledComponents";
import { allTrackers } from "app/services/track/track";
import { AddToDashboard } from "app/components/dashboard/AddToDashboard/AddToDashboard";
import { WidgetTitle } from "app/components/widget-title/WidgetTitle";
import type { IWidgetModelTypesWebSource } from "app/components/widget/widget-types/Widget";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { chosenSites } from "app/common/services/chosenSites";
import { isPaidExcelAllowed } from "app/services/PaidExcelService/isPaidExcelAllowed";

const i18n = i18nFilter();
interface MonetizationNetworksTableTopProps {
    onFilterChange: (...args: any[]) => void;
    searchTerm?: string;
    tableColumns: any[];
    onClickToggleColumns(index: any): void;
    excelLink: string;
    downloadExcelPermitted?: boolean;
    filtersStateObject: any;
    totalCount: number;
}
export const MonetizationNetworksTableTop: FunctionComponent<MonetizationNetworksTableTopProps> = (props) => {
    const [isLoading, setIsLoading] = useState(false);
    const [totalCompetitiveTrafficShare, setTotalCompetitiveTrafficShare] = useState([]);
    const { params } = useCurrentStateAndParams();
    const isCompare = params.key.split(",").length > 1;
    const isPaidExcelPermitted = isPaidExcelAllowed();

    useEffect(() => {
        const fetchData = async () => {
            const fetchService = DefaultFetchService.getInstance();
            const { country, duration, isWWW, key } = params;
            const durationObject = DurationService.getDurationData(duration);
            const { from, to, isWindow } = durationObject.forAPI;
            const compareCompetitionParams = { country, from, isWWW, isWindow, key, to };
            const queryParams = apiHelper.transformParamsForAPI(compareCompetitionParams);
            try {
                const data = await fetchService.get<{
                    TotalVisitsGlobalList: Record<string, number>;
                }>("/api/websiteanalysis/GetTrafficDisplayPaidOutgoingWebsitesTable", queryParams);
                const TotalVisitsGlobalList = data.TotalVisitsGlobalList
                    ? Object.entries<number>(data.TotalVisitsGlobalList).map(([domain, percentage]) => {
                          return {
                              color: chosenSites.getSiteColor(domain),
                              name: domain,
                              percentage,
                              valueText: changeFilter()(percentage),
                              width: percentage * 100,
                          };
                      })
                    : [];
                setIsLoading(false);
                if (TotalVisitsGlobalList) {
                    setTotalCompetitiveTrafficShare(TotalVisitsGlobalList);
                }
            } catch (error) {
                setIsLoading(false);
            }
        };
        if (isCompare) {
            setIsLoading(true);
            fetchData();
        }
    }, [chosenSites, isCompare, params, setIsLoading]);
    const {
        downloadExcelPermitted,
        excelLink,
        tableColumns,
        onClickToggleColumns,
        onFilterChange,
        searchTerm,
        totalCount,
    } = props;

    const { country, /* webSource, */ duration } = params;
    const durationObject = DurationService.getDurationData(duration);
    const is28d = duration === "28d";
    const [fromDate, toDate] = [durationObject.raw.from, durationObject.raw.to];
    const subTitleFilters = [
        {
            filter: "date",
            value: {
                from: fromDate.valueOf(),
                to: toDate.valueOf(),
                useRangeDisplay: !is28d && fromDate.format("YYYY-MM") !== toDate.format("YYYY-MM"),
            },
        },
        {
            filter: "country",
            countryCode: country,
            value: CountryService.getCountryById(country).text,
        },
        {
            filter: "webSource",
            value: devicesTypes.DESKTOP,
        },
    ];
    const excelDownloadUrl = downloadExcelPermitted ? excelLink : "";
    let excelLinkHref = {};
    if (excelDownloadUrl !== "") {
        excelLinkHref = { href: excelDownloadUrl };
    }

    const trackExcelDownload = () => {
        allTrackers.dispatchTrackEvent("Download", "submit-ok", "Table/Excel");
    };

    const onSearch = (searchTerm: string) => {
        allTrackers.dispatchTrackEvent("Search Bar", "click", `Table/${searchTerm}`);
        // filter and urlParams were different by surounding "" in old angular, we keep compatibility
        const filter = searchTerm ? `name;contains;"${searchTerm.trim()}"` : "";
        onFilterChange({ filter });
        const urlParams = searchTerm ? `name;contains;${searchTerm.trim()}` : "";
        proNavigator.applyUpdateParams({
            AdNetwork_filters: urlParams || null,
        });
    };

    const onColumnToggle = (key) => {
        if (tableColumns[key].visible) {
            allTrackers.dispatchTrackEvent("Drop Down", "remove", `Table Columns/${tableColumns[key]?.displayName}`);
        } else {
            allTrackers.dispatchTrackEvent("Drop Down", "add", `Table Columns/${tableColumns[key]?.displayName}`);
        }
        onClickToggleColumns(parseInt(key));
    };

    const getColumnsPickerLiteProps = (): Omit<IColumnsPickerLiteProps, "translations"> => {
        const columns = tableColumns.reduce((res, col, index) => {
            if (!col.fixed) {
                return [
                    ...res,
                    {
                        key: index.toString(),
                        displayName: col.displayName,
                        visible: col.visible,
                    },
                ];
            }
            return res;
        }, []);
        return {
            columns,
            onColumnToggle: onColumnToggle,
            onPickerToggle: () => null,
            maxHeight: 264,
            width: "auto",
        };
    };
    const title = i18n("analysis.monetization.adnetworks.table.title");
    const tooltip = i18n("analysis.monetization.adnetworks.table.title.tooltip");

    return (
        <>
            <TopHeadlineContainer>
                <WidgetTitle
                    title={title}
                    tooltip={tooltip}
                    filters={subTitleFilters}
                >
                    <FlexRow>
                        <DownloadExcelContainer {...excelLinkHref}>
                            <DownloadButtonMenu
                                Excel={true}
                                PaidExcel={isPaidExcelPermitted}
                                totalCount={totalCount}
                                downloadUrl={excelDownloadUrl}
                                exportFunction={trackExcelDownload}
                                excelLocked={!downloadExcelPermitted}
                            />
                        </DownloadExcelContainer>
                        <ColumnsPickerLite
                            {...getColumnsPickerLiteProps()}
                            withTooltip={true}
                            translations={{
                                tooltip: i18n("workspace.sales.column.picker"),
                                getSelectedAmountText: ({ selectedValue, totalValue }) =>
                                    i18n("common.columnsPicker.selectedAmount", {
                                        selected: selectedValue,
                                        total: totalValue,
                                    }),
                                mandatoryField: i18n("common.columnsPicker.mandatoryField"),
                                apply: i18n("common.apply"),
                            }}
                        />
                        <AddToDashboard
                            metric="DashboardTopOutgoingAdNetworks"
                            type="Table"
                            modelType="fromWebsite"
                            webSource={devicesTypes.DESKTOP as IWidgetModelTypesWebSource}
                            filters={{ ...params.filtersStateObject }}
                        />
                    </FlexRow>
                </WidgetTitle>
            </TopHeadlineContainer>
            {isCompare && (isLoading || totalCompetitiveTrafficShare) && (
                <>
                    <Separator />
                    <MetricsCompareBarsContainer>
                        <MetricsCompareBarsTitle>
                            {i18n("analysis.monetization.adnetworks.table.compare.title")}
                        </MetricsCompareBarsTitle>
                        <MetricsCompareBarsStyled
                            items={[{ bars: totalCompetitiveTrafficShare }]}
                            isFetching={isLoading}
                            title={i18n("analysis.monetization.adnetworks.table.compare.tooltip.title")}
                        />
                    </MetricsCompareBarsContainer>
                </>
            )}
            <Separator />
            <SectionContainer>
                <SearchContainer>
                    <SearchInput
                        defaultValue={searchTerm}
                        debounce={500}
                        onChange={onSearch}
                        placeholder={i18n("analysis.monetization.adnetworks.table.search.placeholder")}
                    />
                </SearchContainer>
            </SectionContainer>
        </>
    );
};
